import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PlayCircle, Clock, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';

const VideoLibrary = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(`${API_URL}/api/videos`);
        if (response.ok) {
          const data = await response.json();
          setVideos(data);
        }
      } catch (error) {
        console.error('Failed to fetch videos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [API_URL]);

  const getVideoThumbnail = (url) => {
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (youtubeMatch) {
      return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
    }
    return null;
  };

  return (
    <div data-testid="video-library-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-cream-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
              Resources
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-charcoal leading-tight mb-8">
              Video Library
            </h1>
            <p className="text-xl text-charcoal/70 leading-relaxed">
              Helpful videos on exercises, stretches, and wellness guidance to support 
              your health journey at home.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-sage-600 animate-spin" />
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-16">
              <PlayCircle className="w-16 h-16 text-sage-200 mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-charcoal mb-2">Coming Soon</h3>
              <p className="text-charcoal/60">
                Video content is being prepared. Check back soon!
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/resources/videos/${video.slug || video.id}`}>
                    <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-300 rounded-2xl overflow-hidden group">
                      <CardContent className="p-0">
                        {/* Video thumbnail */}
                        <div className="bg-sage-900 aspect-video flex items-center justify-center relative overflow-hidden">
                          {video.thumbnail_url ? (
                            <img 
                              src={video.thumbnail_url.startsWith('/api') ? `${API_URL}${video.thumbnail_url}` : video.thumbnail_url} 
                              alt={video.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : getVideoThumbnail(video.video_url) ? (
                            <img 
                              src={getVideoThumbnail(video.video_url)} 
                              alt={video.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="absolute inset-0 bg-sage-800 opacity-50" />
                          )}
                          <PlayCircle className="absolute w-16 h-16 text-white/80 group-hover:text-white group-hover:scale-110 transition-all z-10" />
                          {video.duration && (
                            <div className="absolute bottom-3 right-3 bg-charcoal/80 text-white text-xs px-2 py-1 rounded">
                              {video.duration}
                            </div>
                          )}
                        </div>
                        
                        <div className="p-6">
                          <div className="flex items-center gap-4 mb-3">
                            <span className="text-xs font-semibold text-sage-600 uppercase tracking-wide">
                              {video.category}
                            </span>
                            {video.duration && (
                              <span className="flex items-center gap-1 text-xs text-charcoal/50">
                                <Clock className="w-3 h-3" />
                                {video.duration}
                              </span>
                            )}
                          </div>
                          
                          <h2 className="font-serif text-xl font-medium text-charcoal mb-3 group-hover:text-sage-600 transition-colors line-clamp-2">
                            {video.title}
                          </h2>
                          
                          <p className="text-charcoal/60 text-sm leading-relaxed line-clamp-2">
                            {video.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default VideoLibrary;
